import { customRussianMessages } from 'locales/ru/messages';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { Divider } from 'shared/mui/Divider/Divider';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';

import { OlStyled, UlStyled } from './MerchantShowCommissionsListReferenceDialog.styled';

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

type MerchantCommissionsStatusesKeys =
  keyof (typeof customRussianMessages)['app']['merchantCommissions']['reference']['statuses'];
type MerchantCommissionsTypesKeys =
  keyof (typeof customRussianMessages)['app']['merchantCommissions']['reference']['types'];
type MerchantCommissionsTypesDescriptionKeys =
  keyof (typeof customRussianMessages)['app']['merchantCommissions']['reference']['description'];
type MerchantCommissionsPaymentActionsKeys =
  keyof (typeof customRussianMessages)['app']['merchantCommissions']['reference']['roles']['paymentsManager']['actions'];
type MerchantCommissionsPaymentRecalculationActionsKeys =
  keyof (typeof customRussianMessages)['app']['merchantCommissions']['reference']['roles']['paymentsManager']['recalculationActions'];
type MerchantCommissionsFinanceActionsKeys =
  keyof (typeof customRussianMessages)['app']['merchantCommissions']['reference']['roles']['financeManager']['actions'];
type MerchantCommissionsFinanceExtraActionsKeys =
  keyof (typeof customRussianMessages)['app']['merchantCommissions']['reference']['roles']['financeManager']['extraActions'];
type MerchantCommissionsFinanceExtraActionsWithDefenitionKeys =
  keyof (typeof customRussianMessages)['app']['merchantCommissions']['reference']['roles']['financeManager']['extraActionsWithDefenition'];

export const MerchantShowCommissionsListReferenceDialog: React.FC<Props> = ({ open, onClose }) => {
  const translate = useTypedTranslate();

  return (
    <Dialog onClose={onClose} open={open}>
      <Box sx={{ width: 600, maxHeight: 'calc(100vh - 160px)', overflow: 'hidden' }}>
        <CardHeader
          appendActions={<CloseIconButton onClose={onClose} />}
          sx={{ paddingBottom: '24px' }}
          title={translate('app.merchantCommissions.reference.title')}
          titleSize="smallBold"
        />
        <CardContent
          sx={{ paddingTop: '0 !important', maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}
        >
          <Typography sx={{ mb: 2 }} variant="body2">
            {translate('app.merchantCommissions.reference.intro1')}
          </Typography>
          <Typography sx={{ mb: 2 }} variant="body2">
            {translate('app.merchantCommissions.reference.intro2')}
          </Typography>
          <UlStyled>
            {['regular', 'parent', 'child', 'ladder'].map((key) => (
              <Typography component="li" key={key} variant="body2">
                {translate(
                  `app.merchantCommissions.reference.types.${key as MerchantCommissionsTypesKeys}`,
                )}
              </Typography>
            ))}
          </UlStyled>
          {['parent', 'child', 'ladder'].map((key) => (
            <Typography key={key} sx={{ mb: 2 }} variant="body2">
              <TypographyBold
                text={translate(
                  `app.merchantCommissions.reference.description.${key as MerchantCommissionsTypesDescriptionKeys}.name`,
                )}
              />{' '}
              {translate(
                `app.merchantCommissions.reference.description.${key as MerchantCommissionsTypesDescriptionKeys}.text`,
              )}
            </Typography>
          ))}
          <Typography sx={{ mb: 2 }} variant="body2">
            {translate('app.merchantCommissions.reference.intro3')}
          </Typography>
          <OlStyled>
            {['new', 'inApproval', 'rejected', 'approved', 'annulled', 'activated', 'archived'].map(
              (key) => (
                <Typography component="li" key={key} variant="body2">
                  <TypographyBold
                    text={translate(
                      `app.merchantCommissions.reference.statuses.${key as MerchantCommissionsStatusesKeys}.name`,
                    )}
                  />
                  {' – '}
                  {translate(
                    `app.merchantCommissions.reference.statuses.${key as MerchantCommissionsStatusesKeys}.text`,
                  )}
                </Typography>
              ),
            )}
          </OlStyled>
          <Divider sx={{ my: 2, borderColor: 'divider' }} />
          <Typography fontWeight={500} mb={1}>
            {translate('app.merchantCommissions.reference.roles.paymentsManager.title')}
          </Typography>
          <Typography mb={2} variant="body2">
            {translate('app.merchantCommissions.reference.roles.paymentsManager.intro1')}
          </Typography>
          <UlStyled>
            {[
              'create',
              'submit',
              'editRejected',
              'deleteRejected',
              'annul',
              'activate',
              'archive',
            ].map((key) => (
              <Typography component="li" key={key} variant="body2">
                <TypographyBold
                  text={translate(
                    `app.merchantCommissions.reference.roles.paymentsManager.actions.${key as MerchantCommissionsPaymentActionsKeys}.name`,
                  )}
                />
                {' – '}
                {translate(
                  `app.merchantCommissions.reference.roles.paymentsManager.actions.${key as MerchantCommissionsPaymentActionsKeys}.text`,
                )}
              </Typography>
            ))}
          </UlStyled>
          <Typography mt={2} variant="body2">
            {translate('app.merchantCommissions.reference.roles.paymentsManager.intro2.before')}{' '}
            <TypographyBold
              text={translate(
                'app.merchantCommissions.reference.roles.paymentsManager.intro2.merchant',
              )}
            />{' '}
            {translate('app.merchantCommissions.reference.roles.paymentsManager.intro2.between')}{' '}
            <TypographyBold
              text={translate(
                'app.merchantCommissions.reference.roles.paymentsManager.intro2.partner',
              )}
            />
            {', '}
            {translate('app.merchantCommissions.reference.roles.paymentsManager.intro2.after')}
          </Typography>
          <Typography mt={2} variant="body2">
            {translate('app.merchantCommissions.reference.roles.paymentsManager.intro3')}
          </Typography>
          <UlStyled>
            {['create', 'view', 'edit', 'delete'].map((key) => (
              <Typography component="li" key={key} variant="body2">
                {translate(
                  `app.merchantCommissions.reference.roles.paymentsManager.recalculationActions.${key as MerchantCommissionsPaymentRecalculationActionsKeys}`,
                )}
              </Typography>
            ))}
          </UlStyled>
          <Divider sx={{ my: 2, borderColor: 'divider' }} />
          <Typography fontWeight={500} mb={1}>
            {translate('app.merchantCommissions.reference.roles.financeManager.title')}
          </Typography>
          <Typography mb={2} variant="body2">
            {translate('app.merchantCommissions.reference.roles.financeManager.intro1')}
          </Typography>
          <UlStyled>
            {['approve', 'reject'].map((key) => (
              <Typography component="li" key={key} variant="body2">
                <TypographyBold
                  text={translate(
                    `app.merchantCommissions.reference.roles.financeManager.actions.${key as MerchantCommissionsFinanceActionsKeys}.name`,
                  )}
                />
                {' – '}
                {translate(
                  `app.merchantCommissions.reference.roles.financeManager.actions.${key as MerchantCommissionsFinanceActionsKeys}.text`,
                )}
              </Typography>
            ))}
          </UlStyled>
          <Typography mb={2} variant="body2">
            {translate('app.merchantCommissions.reference.roles.financeManager.intro2')}
          </Typography>
          <UlStyled>
            {['create', 'approve', 'editReject', 'deleteReject', 'annulApproved'].map((key) => (
              <Typography component="li" fontWeight={500} key={key} variant="body2">
                {translate(
                  `app.merchantCommissions.reference.roles.financeManager.extraActions.${key as MerchantCommissionsFinanceExtraActionsKeys}`,
                )}
              </Typography>
            ))}
            {['activate', 'archive'].map((key) => (
              <Typography component="li" key={key} variant="body2">
                <TypographyBold
                  text={translate(
                    `app.merchantCommissions.reference.roles.financeManager.extraActionsWithDefenition.${key as MerchantCommissionsFinanceExtraActionsWithDefenitionKeys}.name`,
                  )}
                />
                {' – '}
                {translate(
                  `app.merchantCommissions.reference.roles.financeManager.extraActionsWithDefenition.${key as MerchantCommissionsFinanceExtraActionsWithDefenitionKeys}.text`,
                )}
              </Typography>
            ))}
          </UlStyled>
          <Typography mb={2} variant="body2">
            {translate('app.merchantCommissions.reference.roles.financeManager.intro3')}
          </Typography>
          <UlStyled>
            {['create', 'view', 'edit', 'delete', 'run'].map((key) => (
              <Typography component="li" key={key} variant="body2">
                {translate(
                  `app.merchantCommissions.reference.roles.financeManager.recalculationActions.${key as MerchantCommissionsPaymentRecalculationActionsKeys}`,
                )}
              </Typography>
            ))}
          </UlStyled>
          <Typography mb={2} variant="body2">
            {translate('app.merchantCommissions.reference.roles.financeManager.intro4')}
          </Typography>
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={[
            <Button
              key="back"
              label={translate('app.merchantCommissions.reference.actions.back')}
              onClick={onClose}
              variant="outlined"
            />,
          ]}
        />
      </Box>
    </Dialog>
  );
};
